// 다운로드 진행 상태를 담는 모듈 스코프 스토어. Toast.tsx 의 스토어 패턴을 그대로
// 따른다(records + listeners + emit/subscribe + useSyncExternalStore). 상태를 컴포넌트
// 밖(모듈 스코프)에 두는 게 핵심이다:
//   - 다운로드를 시작한 화면(AttachmentList)을 벗어나도 진행 스피너가 유지된다
//     (앱 루트에 상주하는 <DownloadTray/> 가 이 스토어를 구독한다).
//   - 실제 다운로드도 컴포넌트 수명과 무관하게 계속된다(웹=모듈 스코프 fetch 프라미스,
//     네이티브=별도 프로세스/URLSession).
//
// 웹/네이티브 단일 진입점: startDownload() 하나로 양쪽을 처리한다.
//   - 네이티브: 플러그인이 다운로드하고 "nativeDownloadProgress" 이벤트로 진행률을 준다.
//   - 웹: fetch 스트림을 직접 읽어 Content-Length 대비 진행률을 계산하고, 다 받으면
//         Blob 을 <a download> 로 저장한다(브라우저 기본 다운로드는 진행률을 안 주므로
//         직접 받는다). 대용량은 Blob 이 메모리에 상주하니 주의.

import * as React from "react";
import { loadNativeDownload, type NativeDownloadModule } from "@app/ui/lib/NativeDownload";

export type DownloadStatus = "active" | "done" | "error";

export interface DownloadRecord {
  id: string;
  fileName: string;
  /** 0..1 진행률. null 이면 진행률 불명(불확정 스피너). */
  progress: number | null;
  status: DownloadStatus;
}

export interface StartDownloadInput {
  /** 절대 URL. 호출부에서 new URL(item.url, location.origin).href 로 절대화해 넘긴다. */
  url: string;
  fileName: string;
  contentType?: string;
}

let counter = 0;
function nextId(): string {
  counter += 1;
  return `download-${counter}`;
}

let records: DownloadRecord[] = [];
const listeners = new Set<() => void>();

function emit() {
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot() {
  return records;
}

function patch(id: string, next: Partial<DownloadRecord>) {
  records = records.map((record) => (record.id === id ? { ...record, ...next } : record));
  emit();
}

function remove(id: string) {
  records = records.filter((record) => record.id !== id);
  emit();
}

/** 완료 2.5s / 실패 6s 뒤 자동 정리. 사용자가 결과를 인지할 시간을 준다. */
function scheduleRemoval(id: string, status: DownloadStatus) {
  const delay = status === "error" ? 6000 : 2500;
  setTimeout(() => remove(id), delay);
}

// ── 네이티브 진행률 이벤트: 여러 다운로드가 동시에 흐를 수 있어 event.id 로 라우팅한다.
//    리스너는 첫 네이티브 다운로드에서 한 번만 등록한다.
let nativeListenerReady = false;
async function ensureNativeProgressListener(mod: NativeDownloadModule) {
  if (nativeListenerReady) return;
  nativeListenerReady = true;
  await mod.NativeDownload.addListener("nativeDownloadProgress", (event) => {
    patch(event.id, { progress: event.progress });
  });
}

async function runNative(mod: NativeDownloadModule, id: string, input: StartDownloadInput) {
  await ensureNativeProgressListener(mod);
  await mod.NativeDownload.download({
    id,
    url: input.url,
    fileName: input.fileName,
    contentType: input.contentType,
  });
}

/** 브라우저에서 Blob 을 파일로 저장한다(임시 <a download> 클릭). */
function saveBlob(blob: Blob, fileName: string) {
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = objectUrl;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  // 저장 트리거 후 잠시 뒤 해제(즉시 해제하면 일부 브라우저가 저장을 취소함).
  setTimeout(() => URL.revokeObjectURL(objectUrl), 10000);
}

async function runWeb(id: string, input: StartDownloadInput) {
  const response = await fetch(input.url, { credentials: "include" });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);

  const total = Number(response.headers.get("Content-Length")) || 0;
  const reader = response.body?.getReader();

  // 스트림을 못 읽으면(구형/특수 환경) 진행률 없이 통짜 blob 으로 폴백.
  if (!reader) {
    patch(id, { progress: null });
    saveBlob(await response.blob(), input.fileName);
    return;
  }

  const chunks: Uint8Array[] = [];
  let received = 0;
  for (;;) {
    // 스트림 읽기는 본질적으로 순차라 병렬화 불가(각 read 가 다음 청크를 기다림).
    // eslint-disable-next-line no-await-in-loop
    const { done, value } = await reader.read();
    if (done) break;
    if (value) {
      chunks.push(value);
      received += value.length;
      patch(id, { progress: total > 0 ? received / total : null });
    }
  }

  const type =
    input.contentType || response.headers.get("Content-Type") || "application/octet-stream";
  saveBlob(new Blob(chunks as BlobPart[], { type }), input.fileName);
}

async function run(id: string, input: StartDownloadInput) {
  try {
    const mod = await loadNativeDownload();
    if (mod) await runNative(mod, id, input);
    else await runWeb(id, input);
    patch(id, { progress: 1, status: "done" });
    scheduleRemoval(id, "done");
  } catch (err) {
    console.warn("[download] 실패", err);
    patch(id, { status: "error" });
    scheduleRemoval(id, "error");
  }
}

/**
 * 다운로드를 시작하고 진행 레코드를 스토어에 추가한다. 웹/네이티브 공통 진입점.
 * 즉시 레코드 id 를 반환하고 실제 다운로드는 백그라운드에서 진행된다.
 */
export function startDownload(input: StartDownloadInput): string {
  const id = nextId();
  records = [...records, { id, fileName: input.fileName, progress: 0, status: "active" }];
  emit();
  void run(id, input);
  return id;
}

export function dismissDownload(id: string) {
  remove(id);
}

/** React 구독 훅. <DownloadTray/> 가 사용한다. */
export function useDownloads(): DownloadRecord[] {
  return React.useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
